/** HÂRTIA DE CALC: imaginea pusă SUB desen, ca să se poată trasa peste ea.
 *
 *  DE CE NU E ÎN DOCUMENT. Documentul modulului e catalog: din el se generează
 *  soluții, el se publică, el se compară pe `content_hash`. O fotografie de
 *  plan n-are ce căuta acolo — ar fi un megaoctet purtat de fiecare citire a
 *  colecției, pe toată viața modulului, pentru un ajutor care se aruncă după ce
 *  s-a desenat. Calcul e un OBIECT AL MESEI DE LUCRU, ca panoramarea sau
 *  straturile aprinse: al omului care desenează, nu al modulului desenat.
 *
 *  A STAT ÎN BROWSER (`localStorage`) și n-a ținut: o poză de plan e un
 *  megaoctet, cota unei origini e de câțiva, iar scrierea care nu încăpea
 *  arunca hârtiile celorlalte module și încerca din nou — la al treilea modul
 *  trasat, primul rămânea fără poză, și nimeni nu afla. Acum are un rând al ei
 *  pe server, cerut doar la deschiderea editorului: v. `calcStocare.ts` și
 *  migrarea 0044. Fișierul ăsta a rămas ce era la început — ÎNȚELESUL hârtiei,
 *  fără nimic despre unde se ține.
 *
 *  CALIBRAREA E TOT AICI, lângă imagine, fiindcă fără ea imaginea nu e o
 *  hârtie de calc, ci un tapet: `scara` (metri pe pixel), `x0`/`y0` (unde cade
 *  colțul ei stânga-sus, în metrii modulului) și `rot` fac dintr-un pixel un
 *  punct din plan. De-aici încolo «la 312 px de marginea stângă» și «la 4,10 m
 *  de axă» sunt același lucru — și pentru mâna omului, și pentru asistent.
 */

/** Ce se ține despre imaginea de sub desen. */
import type { Cheie, Valori } from "../../i18n";
export type Calc = {
  /** `data:image/…;base64,…`. */
  imagine: string;
  /** Mărimea ei, în pixeli — cadrul în care se citesc coordonatele. */
  latime: number;
  inaltime: number;
  /** METRI PE PIXEL. `0` = încă necalibrată: se vede, dar nu se măsoară. */
  scara: number;
  /** Unde cade colțul stânga-sus al imaginii, în metrii modulului. */
  x0: number;
  y0: number;
  /** Rotirea imaginii în jurul colțului ei stânga-sus, în grade. O poză de
   *  telefon e strâmbă cu două-trei grade, iar un plan trasat peste ea ar
   *  moșteni strâmbătura în fiecare zid. */
  rot: number;
  /** Cât se vede din ea, 0…1. */
  opacitate: number;
  ascuns: boolean;
};

/** Calcul unei imagini proaspăt alese: se vede, nu se măsoară încă.
 *
 *  Nu se ghicește nicio scară din mărimea în pixeli — un plan scanat la 150 dpi
 *  și unul fotografiat de la un metru au aceeași lățime în pixeli și scări
 *  diferite de zece ori. Necalibrat înseamnă necalibrat, iar primul pas al
 *  importului e chiar ăsta.
 */
export function calcNou(imagine: string, latime: number,
                        inaltime: number): Calc {
  return {
    imagine, latime, inaltime,
    scara: 0, x0: 0, y0: 0, rot: 0,
    opacitate: 0.55, ascuns: false,
  };
}

/** Punctul din imagine (pixeli), în metrii modulului. Fără scară n-are răspuns. */
export function laMetri(c: Calc, px: number, py: number): [number, number] | null {
  if (!c.scara) return null;
  const a = (c.rot * Math.PI) / 180;
  const ux = px * c.scara;
  const uy = py * c.scara;
  return [c.x0 + ux * Math.cos(a) - uy * Math.sin(a),
          c.y0 + ux * Math.sin(a) + uy * Math.cos(a)];
}

/** Drumul invers: un punct al modulului, în pixelii imaginii. */
export function laPixeli(c: Calc, x: number, y: number): [number, number] | null {
  if (!c.scara) return null;
  const a = (-c.rot * Math.PI) / 180;
  const dx = x - c.x0;
  const dy = y - c.y0;
  return [(dx * Math.cos(a) - dy * Math.sin(a)) / c.scara,
          (dx * Math.sin(a) + dy * Math.cos(a)) / c.scara];
}

/** O IMAGINE ALEASĂ DE OM, pregătită pentru calc: micșorată și recomprimată.
 *
 *  DOUĂ MĂRIMI, și diferența dintre ele e chiar rostul lor. Ce pleacă spre model
 *  ca bloc de imagine se micșorează la 1600 px, fiindcă API-ul taie oricum pe la
 *  1568 și nimeni nu plătește pixeli aruncați. Ce se ține în calc rămâne mai
 *  mare (implicit 2400 px): din el se decupează măririle pe care asistentul le
 *  cere ca să citească o cotă scrisă cu 9 puncte pe un plan A3 — la 1600 px
 *  cifrele alea sunt deja o pată.
 *
 *  JPEG, nu PNG: un plan scanat are zgomot de hârtie, iar PNG-ul îl păstrează
 *  fidel, cu tot cu octeți. Cota se citește la fel de bine la 0,82.
 */
/** IMAGINEA DE CITIT, așa cum a venit — fără micșorare și fără JPEG.
 *
 *  `imagineDinFisier` face hârtia de CALC: o poză ușoară, recomprimată, bună de
 *  pus sub desen. Pentru citire e exact ce nu trebuie: la calitatea 0,82 JPEG-ul
 *  întinde fiecare linie de un pixel pe trei, cu un halou gri, iar fața unui zid
 *  de rigips se măsoară tocmai pe linia aia. Deci la import pleacă fișierul
 *  întreg; numai unul uriaș (peste `maxOcteti`) se micșorează — și atunci în
 *  PNG, fără pierderi, la 4000 px pe latura mare.
 */
export function originalDinFisier(f: File, maxOcteti = 25_000_000): Promise<string> {
  return new Promise((da, nu) => {
    if (!f.type.startsWith("image/")) { nu(new Error("nu e imagine")); return; }
    if (f.size > maxOcteti) {
      const sursa = URL.createObjectURL(f);
      const poza = new Image();
      poza.onload = () => {
        URL.revokeObjectURL(sursa);
        const k = Math.min(1, 4000 / Math.max(poza.width, poza.height));
        const panza = document.createElement("canvas");
        panza.width = Math.max(1, Math.round(poza.width * k));
        panza.height = Math.max(1, Math.round(poza.height * k));
        const c = panza.getContext("2d");
        if (!c) { nu(new Error("fără pânză")); return; }
        c.drawImage(poza, 0, 0, panza.width, panza.height);
        try { da(panza.toDataURL("image/png")); } catch (e) { nu(e as Error); }
      };
      poza.onerror = () => { URL.revokeObjectURL(sursa); nu(new Error("imaginea nu s-a încărcat")); };
      poza.src = sursa;
      return;
    }
    const cititor = new FileReader();
    cititor.onload = () => da(String(cititor.result));
    cititor.onerror = () => nu(new Error("fișierul nu s-a citit"));
    cititor.readAsDataURL(f);
  });
}

export function imagineDinFisier(
  f: File, maxPx = 2400, calitate = 0.82,
): Promise<{ url: string; latime: number; inaltime: number }> {
  return new Promise((da, nu) => {
    if (!f.type.startsWith("image/")) { nu(new Error("nu e imagine")); return; }
    const sursa = URL.createObjectURL(f);
    const poza = new Image();
    poza.onload = () => {
      URL.revokeObjectURL(sursa);
      const k = Math.min(1, maxPx / Math.max(poza.width, poza.height));
      const panza = document.createElement("canvas");
      panza.width = Math.max(1, Math.round(poza.width * k));
      panza.height = Math.max(1, Math.round(poza.height * k));
      const c = panza.getContext("2d");
      if (!c) { nu(new Error("fără pânză")); return; }
      c.drawImage(poza, 0, 0, panza.width, panza.height);
      try {
        da({ url: panza.toDataURL("image/jpeg", calitate),
             latime: panza.width, inaltime: panza.height });
      } catch (e) { nu(e as Error); }
    };
    poza.onerror = () => { URL.revokeObjectURL(sursa); nu(new Error("imaginea nu s-a încărcat")); };
    poza.src = sursa;
  });
}

/** Dreptunghiul păstrat din planșă, în FRACȚIUNI din imagine (0…1) — nu în
 *  pixeli de ecran, fiindcă ecranul de decupare arată poza micșorată. */
export type Decupaj = { x: number; y: number; w: number; h: number };

export const DECUPAJ_INTREG: Decupaj = { x: 0, y: 0, w: 1, h: 1 };

/** Decupajul întreg nu e un decupaj: fișierul pleacă neatins, fără nicio
 *  recomprimare. Sub o miime din latură nu se vede pe ecran, deci nu contează. */
export const eIntreg = (d: Decupaj) =>
  d.x < 1e-3 && d.y < 1e-3 && d.w > 1 - 2e-3 && d.h > 1 - 2e-3;

/** Taie din fișier dreptunghiul ales, LA REZOLUȚIA LUI ÎNTREAGĂ, și îl dă înapoi
 *  ca PNG — fără pierderi, fiindcă pe liniile lui se măsoară zidurile
 *  (v. `originalDinFisier`). Din el se fac apoi și calcul, și originalul. */
export function decupeaza(f: File, d: Decupaj): Promise<File> {
  if (eIntreg(d)) return Promise.resolve(f);
  return new Promise((da, nu) => {
    const sursa = URL.createObjectURL(f);
    const poza = new Image();
    poza.onload = () => {
      URL.revokeObjectURL(sursa);
      const sx = Math.round(d.x * poza.width);
      const sy = Math.round(d.y * poza.height);
      const sw = Math.max(1, Math.round(d.w * poza.width));
      const sh = Math.max(1, Math.round(d.h * poza.height));
      const panza = document.createElement("canvas");
      panza.width = sw;
      panza.height = sh;
      const c = panza.getContext("2d");
      if (!c) { nu(new Error("fără pânză")); return; }
      c.drawImage(poza, sx, sy, sw, sh, 0, 0, sw, sh);
      panza.toBlob((b) => {
        if (!b) { nu(new Error("decupajul nu s-a scris")); return; }
        da(new File([b], f.name.replace(/\.[^.]*$/, "") + "-decupat.png",
                    { type: "image/png" }));
      }, "image/png");
    };
    poza.onerror = () => { URL.revokeObjectURL(sursa); nu(new Error("imaginea nu s-a încărcat")); };
    poza.src = sursa;
  });
}

/** CÂT DIN CITIRE S-A FĂCUT, în procente, pentru bară. Serverul nu știe dinainte
 *  câte ture îi trebuie agentului, deci procentul e o ESTIMARE pe timp: curba
 *  se apropie de capăt fără să-l atingă (la 2,5 min ~60 %, la 5 min ~85 %), iar
 *  capătul îl dă doar starea serverului — `gata` (se scrie modulul) și `scris`.
 *  O bară care stă la 100 % încă un minut ar minți mai tare decât una care
 *  încetinește. */
export function procentCitire(secunde: number, stare?: string): number {
  if (stare === "scris") return 100;
  if (stare === "gata") return 98;
  const p = 95 * (1 - Math.exp(-Math.max(0, secunde) / 150));
  return Math.max(1, Math.min(95, p));
}

/** LA CE PAS E CITIREA, pentru rândul de sub bară.
 *
 *  Harta e scrisă pe litere, nu compusă din șir (`nou.pas.${pas}`): cheile de
 *  limbă sunt verificate de compilator, iar un pas nou adăugat pe server fără
 *  traducere trebuie să nu scrie nimic pe ecran, nu să scrie cheia. */
const PAS: Record<string, Cheie> = {
  la_rand: "nou.pas.la_rand",
  goluri_pe_ziduri: "nou.pas.goluri_pe_ziduri",
  lipeste_fete: "nou.pas.lipeste_fete",
  aliniaza_ziduri: "nou.pas.aliniaza_ziduri",
  inchide_noduri: "nou.pas.inchide_noduri",
  stalpi_praguri: "nou.pas.stalpi_praguri",
  cote: "nou.pas.cote",
  sonda_scarii: "nou.pas.sonda_scarii",
  potriveste_rotunjeste: "nou.pas.potriveste_rotunjeste",
  camere: "nou.pas.camere",
  mobilier: "nou.pas.mobilier",
  scara: "nou.pas.scara",
  asociaza: "nou.pas.asociaza",
  portanta: "nou.pas.portanta",
  mobila_din_ziduri: "nou.pas.mobila_din_ziduri",
  tocurile_golurilor: "nou.pas.tocurile_golurilor",
};

const ETAPA: Record<string, Cheie> = {
  pregatire: "nou.etapa.pregatire",
  cote: "nou.etapa.cote",
  citire: "nou.etapa.citire",
  finalizare: "nou.etapa.finalizare",
};

/** Pașii finalizării, pentru proba care cere ca fiecare să aibă traducere.
 *  Derivați din hartă, nu scriși a doua oară: două liste ar fi putut rămâne
 *  una în urma celeilalte. */
export const PASII_FINALIZARII = Object.keys(PAS);

/** Textul de sub bară.
 *
 *  Un pas pe care nu-l cunoaștem CADE PE ETICHETA ETAPEI, nu pe nimic: dacă
 *  serverul capătă un pas nou înaintea traducerii lui, «Se așază planul pe
 *  cerneală» spune mai mult decât un rând gol. `null` abia când nu se știe
 *  nici etapa — atunci bara rămâne cum era. */
export function pasulCitirii(
  t: (cheie: Cheie, valori?: Valori) => string,
  etapa?: { etapa?: string; tura?: number; pas?: string } | null,
): string | null {
  if (!etapa?.etapa) return null;
  // Pasul dinăuntrul finalizării bate eticheta etapei: el e cel care ține
  // minute, deci el e cel pe care omul vrea să-l vadă.
  const cheie = (etapa.pas && PAS[etapa.pas]) || ETAPA[etapa.etapa];
  if (!cheie) return null;
  const text = t(cheie);
  return etapa.tura ? t("nou.pas_tura", { pas: text, tura: etapa.tura }) : text;
}
